import { useEffect, useState } from "react";
import { RADIO_OPTIONS, Variant } from "../../utils/enums.js";
import { filterAssignCombinations } from "../../utils/formUtility.ts";
import "./CreateMatterForm.css";
import "../../css/form.css";
import { IQuestionType, valueLabelPair } from "@/types/SPTypes.tsx";
import { useForm, useWatch } from "react-hook-form";
import {
  allSectionsRadioTooltip,
  clientTypeTooltip,
  operationRadioTooltip,
  questionTypeTooltip,
  sectionTooltip,
  systemRadioTooltip
} from "@/utils/tooltipTexts.tsx";
import { formatUsername, validateEmail } from "@/utils/utils.ts";
import { useAppSelector } from "@/hooks/storeHooks.ts";
import {
  ButtonComponent,
  DropZoneComponent,
  InputComponent,
  RadioComponent,
  SelectComponent,
  TextAreaComponent,
  TooltipComponent
} from "fmv-component-ui-library";
export interface CreateMatterFormValues {
  userName: string;
  email: string;
  clientType: valueLabelPair<number> | null;
  questionType: valueLabelPair<number> | null;
  section: valueLabelPair<number> | null;
  selectedRadioOption: string;
  questionTitle: string;
  questionContent: string;
  uploadedFiles: File[];
  teamcenterNumber: string;
}
const CreateMatterForm = ({
  onSubmit
}: {
  onSubmit: (data: CreateMatterFormValues) => void;
}) => {
  const data = useAppSelector(state => state.application);
  const isLoggedIn = useAppSelector(state => state.application.isLoggedIn);
  const filterForm = useAppSelector(state => state.form);
  const [isTeamcenterRequired, setIsTeamcenterRequired] = useState(false);
  const initialFormState = {
    userName: formatUsername(data.currentUser?.Title ?? ""),
    email: data.currentUser?.Email || "",
    clientType: filterForm.clientType || null,
    questionType: filterForm.questionType || null,
    section: filterForm.section || null,
    selectedRadioOption: filterForm.selectedRadioOption || RADIO_OPTIONS.ALL,
    questionTitle: "",
    questionContent: "",
    uploadedFiles: [],
    teamcenterNumber: ""
  };
  const { control, reset, setValue, handleSubmit, watch } =
    useForm<CreateMatterFormValues>({
      mode: "onSubmit",
      defaultValues: initialFormState
    });
  const values = watch();
  const questionType = useWatch({ control, name: "questionType" });
  const clientType = useWatch({ control, name: "clientType" });
  const section = useWatch({ control, name: "section" });
  const selectedRadioOption = useWatch({
    control,
    name: "selectedRadioOption"
  });
  const questionTitle = useWatch({ control, name: "questionTitle" });
  const questionContent = useWatch({ control, name: "questionContent" });
  const [sectionOptions, questionTypeOptions, clientTypeOptions] =
    filterAssignCombinations(
      section,
      clientType,
      questionType,
      selectedRadioOption,
      data
    );
  const onSubmitInternal = async (data: CreateMatterFormValues) => {
    try {
      await onSubmit(data);
      reset();
    } catch (err) {
      console.error("Submit failed", err);
    }
  };
  // to set username and email on reload etc
  useEffect(() => {
    if (data.currentUser) {
      reset({
        userName: formatUsername(data.currentUser.Title ?? ""),
        email: data.currentUser.Email || "",
        clientType: filterForm.clientType || null,
        questionType: filterForm.questionType || null,
        section: filterForm.section || null,
        selectedRadioOption:
          filterForm.selectedRadioOption || RADIO_OPTIONS.ALL,
        questionTitle: "",
        questionContent: "",
        uploadedFiles: [],
        teamcenterNumber: ""
      });
    }
  }, [data.currentUser, reset, filterForm]);
  //If selected questiontype have a template, fill textarea for questionContent
  useEffect(() => {
    if (questionType) {
      const foundQuestionType = data.questionTypes.find(
        (questionTypeFromData: IQuestionType) =>
          questionTypeFromData.questionTitle === questionType.label &&
          questionTypeFromData.questionTemplate
      );
      setValue(
        "questionContent",
        foundQuestionType?.questionTemplate ?? values.questionContent
      );
    } else {
      setValue("questionContent", "");
    }
  }, [questionType, data.questionTypes, setValue]);
  useEffect(() => {
    const normalizeText = (text: string) =>
      text.toLowerCase().replace(/\s+/g, "");
    const normalizedTitle = normalizeText(questionTitle);
    const normalizedContent = questionContent
      ? normalizeText(questionContent)
      : "";
    const shouldRequireTeamcenter =
      section?.label === "Teamcenter" ||
      normalizedTitle.includes("teamcenter") ||
      normalizedContent.includes("teamcenter");
    setIsTeamcenterRequired(Boolean(shouldRequireTeamcenter));
  }, [questionTitle, questionContent, section]);
  return (
    <div>
      <form onSubmit={handleSubmit(onSubmitInternal)}>
        <div className="create-matter-form-row">
          <InputComponent
            control={control}
            name="userName"
            label="Namn *"
            placeholder="För- och efternamn"
            rules={{ required: "Vänligen ange för- och efternamn" }}
          />
          <InputComponent
            control={control}
            name="email"
            label="E-post: *"
            placeholder="Ange e-postadress"
            rules={{
              validate: (value: string) => {
                if (!value) return "Vänligen ange en e-post";
                if (typeof value === "string") {
                  return validateEmail(value) || "Ogiltig e-postadress";
                }
                return "Ogiltig datatyp för e-postfält";
              }
            }}
          />
        </div>
        <div className="create-matter-form-row">
          <TooltipComponent content={questionTypeTooltip} placement="top">
            <SelectComponent
              name="questionType"
              control={control}
              label="Min fråga handlar om:"
              options={questionTypeOptions}
              isClearable
            />
          </TooltipComponent>
          <TooltipComponent content={clientTypeTooltip} placement="top">
            <SelectComponent
              name="clientType"
              control={control}
              label="Jag tillhör"
              options={clientTypeOptions}
              isClearable
            />
          </TooltipComponent>
        </div>
        <div className="create-matter-form-row">
          <div>
            <TooltipComponent content={sectionTooltip} placement="top">
              <SelectComponent
                name="section"
                control={control}
                label="Vilket tjänsteområde eller tekniskt system gäller det?"
                options={sectionOptions}
                isClearable
              />
            </TooltipComponent>
            <div className="radion-btn-container">
              <TooltipComponent content={systemRadioTooltip} placement="top">
                <RadioComponent
                  label="Tekniskt system"
                  name="selectedRadioOption"
                  value={RADIO_OPTIONS.SYSTEM}
                  control={control}
                />
              </TooltipComponent>
              <TooltipComponent content={operationRadioTooltip} placement="top">
                <RadioComponent
                  label="Tjänsteområde"
                  name="selectedRadioOption"
                  value={RADIO_OPTIONS.OPERATION}
                  control={control}
                />
              </TooltipComponent>
              <TooltipComponent
                content={allSectionsRadioTooltip}
                placement="top"
              >
                <RadioComponent
                  label="Alla"
                  name="selectedRadioOption"
                  value={RADIO_OPTIONS.ALL}
                  control={control}
                />
              </TooltipComponent>
            </div>
          </div>
          {isTeamcenterRequired && (
            <InputComponent
              control={control}
              name="teamcenterNumber"
              label="Ange ärendenummer *"
              placeholder="Ange ärendenummer..."
              rules={{ required: "Vänligen ange ett ärendenummer" }}
            />
          )}
        </div>
        <div className="create-matter-form-input-row">
          <InputComponent
            control={control}
            name="questionTitle"
            label="Titel *"
            placeholder="Titel"
            rules={{ required: "Vänligen ange en titel" }}
          />
        </div>
        <div className="create-matter-form-input-row">
          <TextAreaComponent
            control={control}
            name="questionContent"
            label="Beskrivning: *"
            placeholder="Beskrivning..."
            rules={{ required: "Vänligen ange en beskrivning av din fråga" }}
            rows={7}
          />
        </div>
        {isLoggedIn && (
          <div className="create-matter-form-input-row">
            <DropZoneComponent name="uploadedFiles" control={control} />
          </div>
        )}
        <div className="create-matter-form-btn-container">
          <p>
            Du får en bekräftelse via e-post när ärendet har registrerats. Vi
            återkopplar så snart som möjligt!
          </p>
          <ButtonComponent
            value="Send"
            onClick={handleSubmit(onSubmitInternal)}
            label="Skicka"
            variant={Variant.SUCCESS}
          />
        </div>
      </form>
    </div>
  );
};
export default CreateMatterForm;
